import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronDown, ChevronRight, DollarSign, ClipboardList, Route, Users } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { apiClient } from '../../api/client';
import type { ApiResponse } from '../../types/api-manifest';
import { unwrap } from '../../lib/api-envelope';
import { formatCalendarDate, tenantTodayDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { useCollapsibleGroups } from '../../hooks/useCollapsibleGroups';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { fmtCurrency, fmtQty, parseMoney, sumMoney } from '../../lib/display-labels';
import { LOADS, missingRateNote } from '../../lib/missing-rate-note';

/**
 * Driver Pay Detail: each driver's delivered and signed loads in a date range,
 * from GET /trucking/reports/driver-pay-detail.
 *
 * PAY IS DRIVER PAY, AND IT IS ALL OR NOTHING (api#851 + api#853, #815).
 * - A load's `amount` is the driver's recorded `trucking_driver_pay` for that
 *   BOL, or null when none is recorded. It used to be what the CUSTOMER is
 *   billed for the load, which is why this report was withheld.
 * - `summary.gross_pay` sums those amounts and is null if any load has no
 *   recorded pay, with `pay_unrecorded_count` saying how many. Draft pay
 *   (recorded, not yet approved) is included; `pay_draft_count` says how much.
 * - `distance_km` is null when no kilometres were recorded, and `total_km` is
 *   null if any load lacks them (`km_missing_count`).
 * - GROSS ONLY. `deductions` and `net_pay` are null on purpose: trucking
 *   records no deductions anywhere, so there is no net to show. There is no
 *   Deductions or Net column, not even as a dash, until a deductions source
 *   exists (#815, "Trucking pay deductions", a product gap rather than a bug).
 * A null figure is a dash with the API's count beside it, never a partial sum.
 */

const RATE_TYPE_LABELS: Record<string, string> = {
  per_load: 'Per load',
  per_hour: 'Hourly',
  day_rate: 'Day rate',
  per_km: 'Per km',
  per_m3: 'Per m³',
};
/** The billing basis, from the `billing_type` enum; anything else is a dash, not a guess. */
const rateTypeLabel = (t: string | null | undefined) => (t && RATE_TYPE_LABELS[t] ? RATE_TYPE_LABELS[t] : '—');

interface LoadRow {
  load_id: string;
  bol_number: string | null;
  date: string | null;
  origin: string | null;
  destination: string | null;
  rate_type: string | null;
  distance_km: number | null;
  /** The driver's recorded pay for the load, or null when none is recorded. */
  amount: number | null;
  pay_status: string | null;
}
interface DriverGroup {
  driver_id: string;
  name: string | null;
  loads: LoadRow[];
  total_loads: number | null;
  gross_pay: number | null;
  pay_unrecorded_count: number | null;
  pay_draft_count: number | null;
  total_km: number | null;
  km_missing_count: number | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));
function toDriver(d: Record<string, unknown>, i: number): DriverGroup {
  const loads = Array.isArray(d.loads) ? (d.loads as Record<string, unknown>[]) : [];
  const summary = (d.summary ?? {}) as Record<string, unknown>;
  const total = Number(summary.total_loads);
  return {
    driver_id: str(d.driver_id) ?? `driver-${i}`,
    name: str(d.name),
    total_loads: Number.isFinite(total) ? total : loads.length,
    gross_pay: parseMoney(summary.gross_pay),
    pay_unrecorded_count: parseMoney(summary.pay_unrecorded_count),
    pay_draft_count: parseMoney(summary.pay_draft_count),
    total_km: parseMoney(summary.total_km),
    km_missing_count: parseMoney(summary.km_missing_count),
    loads: loads.map((l) => ({
      load_id: String(l.load_id ?? ''),
      bol_number: str(l.bol_number),
      date: str(l.date),
      origin: str(l.origin),
      destination: str(l.destination),
      rate_type: str(l.rate_type),
      distance_km: parseMoney(l.distance_km),
      amount: parseMoney(l.amount),
      pay_status: str(l.pay_status),
    })),
  };
}

const fmtDay = (d: string | null) => formatCalendarDate(d) ?? '—';
const fmtKm = (km: number | null) => (km === null ? '—' : `${fmtQty(km)} km`);
const NO_PAY = { one: 'load', many: 'loads' };
/** "2 loads have no recorded pay", or null. */
const unrecordedNote = (n: unknown) => missingRateNote(n, NO_PAY, 'no recorded pay');
const kmNote = (n: unknown) => missingRateNote(n, LOADS, 'no kilometres');

export default function DriverPayDetailReport() {
  const navigate = useNavigate();
  // The TENANT's current month (companies.time_zone), not the device's. No
  // zone, no seeded range: the inputs start blank rather than guess.
  const timeZone = useTenantTimeZone();
  const now = tenantTodayDate(timeZone);
  const [startDate, setStartDate] = useState(now ? format(startOfMonth(now), 'yyyy-MM-dd') : '');
  const [endDate, setEndDate] = useState(now ? format(endOfMonth(now), 'yyyy-MM-dd') : '');
  const [driverId, setDriverId] = useState('');

  const reportQuery = useQuery({
    queryKey: ['driver-pay-detail', startDate, endDate, driverId],
    queryFn: () => {
      const params: Record<string, string> = { start_date: startDate, end_date: endDate };
      if (driverId) params.driver_id = driverId;
      return apiClient
        .get<ApiResponse<'GET /api/v1/trucking/reports/driver-pay-detail'>>('/trucking/reports/driver-pay-detail', { params })
        .then((r) => unwrap<Record<string, unknown>>(r.data) ?? {});
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(reportQuery) != null;
  const loading = reportQuery.isLoading && !failed;
  const drivers = useMemo(
    () =>
      failed || !reportQuery.data
        ? undefined
        : (Array.isArray(reportQuery.data.drivers) ? (reportQuery.data.drivers as Record<string, unknown>[]) : []).map(toDriver),
    [failed, reportQuery.data],
  );

  // The endpoint returns no driver list, so the filter's options are captured
  // from an unfiltered response: once a driver is selected the server sends
  // back only that one, so the roster has to be remembered.
  const [driverOptions, setDriverOptions] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (driverId || !drivers) return;
    setDriverOptions(drivers.map((d) => ({ id: d.driver_id, name: d.name ?? '—' })));
  }, [drivers, driverId]);

  // ALL OPEN by default: the page is opened to read the loads, a driver filter
  // narrows it to one person, and a tenant's roster is a handful of drivers.
  // Collapse all / Expand all is the escape for a long one.
  const { isCollapsed, toggleGroup, expandAll, collapseAll } = useCollapsibleGroups({ storageKey: 'driver-pay-detail-groups' });

  const totalLoads = drivers ? drivers.reduce((s, d) => s + (d.total_loads ?? 0), 0) : null;
  // Across the drivers shown, all or nothing: one driver whose gross is unknown
  // and the total is a dash. Each driver's figure is the API's.
  const grossTotal = drivers ? sumMoney(drivers.map((d) => d.gross_pay)) : null;
  const kmTotal = drivers ? sumMoney(drivers.map((d) => d.total_km)) : null;
  const unrecordedTotal = drivers ? sumMoney(drivers.map((d) => d.pay_unrecorded_count)) : null;
  const grossNote = drivers && grossTotal === null ? unrecordedNote(unrecordedTotal) : null;
  const fig = (n: number | null) => (failed ? '—' : displayValue(n));

  const inputCls =
    'rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none';
  const groupId = (d: DriverGroup) => `driver-pay-${d.driver_id}`;

  return (
    <div data-testid="driver-pay-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking reports"
        title="Driver Pay Detail"
        onBack={() => navigate('/reports')}
        backLabel="Back"
        stats={[
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Drivers', sub: 'With loads', tone: 'neutral', loading, value: fig(drivers ? drivers.length : null) },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Loads', sub: 'Delivered or signed', tone: 'info', loading, value: fig(totalLoads) },
          { icon: <DollarSign className="size-4" aria-hidden="true" />, label: 'Gross Pay', sub: 'Recorded pay', tone: 'accent', loading, value: failed ? '—' : fmtCurrency(grossTotal) },
          { icon: <Route className="size-4" aria-hidden="true" />, label: 'Distance', sub: 'Kilometres', tone: 'success', loading, value: failed ? '—' : fmtKm(kmTotal) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {grossNote && (
            <p data-testid="gross-note" className="text-xs text-fieldiq-muted">
              Gross pay is not shown: {grossNote}.
            </p>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs font-semibold text-fieldiq-muted">
              From
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold text-fieldiq-muted">
              To
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold text-fieldiq-muted">
              Driver
              <select value={driverId} onChange={(e) => setDriverId(e.target.value)} className={inputCls}>
                <option value="">All drivers</option>
                {driverOptions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </label>
          </div>

          <section
            aria-labelledby="driver-pay-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Loads by driver</p>
                <h2 id="driver-pay-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                  {startDate && endDate ? `${fmtDay(startDate)} – ${fmtDay(endDate)}` : 'Pick a date range'}
                </h2>
              </div>
              {drivers && drivers.length > 0 && (
                <div className="flex gap-2">
                  <button type="button" onClick={expandAll} className="text-xs font-semibold text-fieldiq-text underline">Expand all</button>
                  <button type="button" onClick={collapseAll} className="text-xs font-semibold text-fieldiq-text underline">Collapse all</button>
                </div>
              )}
            </header>

            {failed ? (
              <LoadFailureNotice error={queryFailureError(reportQuery)} subject="driver pay" allowed="admins and office staff" />
            ) : loading ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : (drivers ?? []).length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">No delivered or signed loads for this period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                    <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">BOL</th>
                      <th className="px-4 py-3">Route</th>
                      <th className="px-4 py-3">Billing basis</th>
                      <th className="px-4 py-3 text-right">Distance</th>
                      <th className="px-4 py-3 text-right">Pay</th>
                    </tr>
                  </thead>
                  {(drivers ?? []).map((d) => {
                    const gid = groupId(d);
                    const shut = isCollapsed(gid);
                    const count = d.total_loads ?? d.loads.length;
                    const name = d.name ?? '—';
                    return (
                      <React.Fragment key={gid}>
                        <tbody>
                          {/* A group header is not a record: transparent rail, no row target. The toggle is the button. */}
                          <tr className="border-l-4 border-t border-fieldiq-border border-l-transparent bg-fieldiq-dark/20">
                            <td colSpan={6} className="p-0">
                              <div className="flex flex-wrap items-center justify-between gap-x-4 pr-4">
                                <button
                                  type="button"
                                  aria-expanded={!shut}
                                  aria-controls={gid}
                                  aria-label={`${name}, ${count} load${count === 1 ? '' : 's'}`}
                                  onClick={() => toggleGroup(gid)}
                                  className="flex flex-1 items-center gap-2 px-4 py-3 text-left"
                                >
                                  {shut ? <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /> : <ChevronDown className="size-4 text-fieldiq-muted" aria-hidden="true" />}
                                  <span className="text-sm font-extrabold text-fieldiq-text">{name}</span>
                                  <span aria-hidden="true" className="text-xs tabular-nums text-fieldiq-muted">{count} load{count === 1 ? '' : 's'}</span>
                                </button>
                                {/* The driver's gross, shown whether the group is open or shut. */}
                                <div data-testid="driver-gross" className="py-2 text-right">
                                  <p className="text-sm tabular-nums text-fieldiq-text">
                                    <span className="text-xs text-fieldiq-muted">Gross </span>
                                    <span className="font-extrabold">{fmtCurrency(d.gross_pay)}</span>
                                    <span className="text-xs text-fieldiq-muted"> · {fmtKm(d.total_km)}</span>
                                  </p>
                                  {[
                                    d.gross_pay === null ? unrecordedNote(d.pay_unrecorded_count) : null,
                                    d.total_km === null ? kmNote(d.km_missing_count) : null,
                                    d.gross_pay !== null && (d.pay_draft_count ?? 0) > 0
                                      ? `Includes ${d.pay_draft_count} not yet approved`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .map((n) => (
                                      <p key={n as string} className="text-xs text-fieldiq-muted">{n}</p>
                                    ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                        <tbody id={gid} hidden={shut}>
                          {!shut &&
                            d.loads.map((l, i) => (
                              <tr key={l.load_id || i} data-testid="driver-pay-load" className="border-l-4 border-t border-fieldiq-border border-l-transparent">
                                {/* `date` is the load's scheduled_date, a calendar day. */}
                                <td className="whitespace-nowrap px-4 py-2.5 text-xs text-fieldiq-muted">{fmtDay(l.date)}</td>
                                <td className="whitespace-nowrap px-4 py-2.5 font-mono text-sm font-bold text-fieldiq-text">{l.bol_number ?? '—'}</td>
                                <td className="px-4 py-2.5">
                                  <div className="flex items-center gap-1.5 text-xs text-fieldiq-muted">
                                    <span>{l.origin ?? '—'}</span>
                                    <ArrowRight className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                                    <span>{l.destination ?? '—'}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 text-xs text-fieldiq-text">{rateTypeLabel(l.rate_type)}</td>
                                <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-text">{fmtKm(l.distance_km)}</td>
                                {/* The driver's pay for this load; never the customer's billed value. */}
                                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums text-fieldiq-text">
                                  {fmtCurrency(l.amount)}
                                  {l.amount !== null && l.pay_status === 'draft' && (
                                    <span className="ml-1.5 text-xs text-fieldiq-muted">Draft</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </React.Fragment>
                    );
                  })}
                </table>
              </div>
            )}
            {drivers && drivers.length > 0 && (
              <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                {fig(totalLoads)} delivered or signed loads, {drivers.length} driver{drivers.length === 1 ? '' : 's'}. Only admins and office staff can open this report.
              </p>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
